import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Upload, Crown, Lock, X, Copy, Check } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpeedRunQuizSetup = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [createdSession, setCreatedSession] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const addOptionTimeRef = useRef({});
  const addQuestionTimeRef = useRef(0);
  
  // Basic session data
  const [topic, setTopic] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [timeLimit, setTimeLimit] = useState(5);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [maxParticipants, setMaxParticipants] = useState(50);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState('');
  const [isPrivate, setIsPrivate] = useState(false); // Public by default
  
  // Questions data
  const [questions, setQuestions] = useState([
    {
      id: Date.now().toString(),
      text: '',
      image_url: null,
      response_type: 'mcq_response',
      options: [
        { id: Date.now().toString() + '_1', text: '', image_url: null },
        { id: Date.now().toString() + '_2', text: '', image_url: null }
      ],
      randomize_options: false,
      correct_answer: null,
      points: 10,
      points_deducted: 0,
      time_limit: 30,
      order: 0
    }
  ]);
  
  // Auto-save function with debounce (returns the draft ID)
  const autoSaveDraft = async () => {
    try {
      setAutoSaving(true);
      const token = localStorage.getItem('token');
      const sessionData = {
        session_type: 'speed_run_quiz',
        topic: topic || 'Untitled Quiz',
        hashtags: hashtags.length > 0 ? hashtags : ['quiz'],
        time_limit: 600,
        is_anonymous: isAnonymous,
        max_participants: maxParticipants,
        questions: questions,
        show_scores: false,
        allow_retry: false,
        hidden_session: isPrivate
      };

      let savedDraftId = draftId;
      
      if (savedDraftId) {
        // Update existing draft
        await axios.put(
          `${API}/sessions/${savedDraftId}`,
          sessionData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } else {
        // Create new draft
        const response = await axios.post(
          `${API}/sessions`,
          sessionData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        savedDraftId = response.data.id;
        setDraftId(savedDraftId);
        console.log('✓ Draft created:', savedDraftId);
      }
      
      setLastSaved(new Date());
      setAutoSaving(false);
      return savedDraftId; // Return the draft ID
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaving(false);
      throw error;
    }
  };

  // Trigger auto-save when form data changes
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Only auto-save if we have minimum data
    if (topic || questions.length > 0) {
      autoSaveTimerRef.current = setTimeout(() => {
        autoSaveDraft();
      }, 2000); // Save 2 seconds after last change
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [topic, hashtags, questions, isAnonymous, maxParticipants]);

  const questionTimeLimitOptions = [
    { value: 15, label: '15 sec' },
    { value: 30, label: '30 sec' },
    { value: 60, label: '1 min' }
  ];

  useEffect(() => {
    fetchProfile();
    loadDraftFromUrl();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile');
    }
  };

  const loadDraftFromUrl = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const draftIdParam = urlParams.get('draft');
    
    if (draftIdParam) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/sessions/${draftIdParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const draftData = response.data;
        
        // Load all fields from the draft
        setDraftId(draftIdParam);
        setTopic(draftData.topic || '');
        setHashtags(draftData.hashtags || []);
        setIsAnonymous(draftData.is_anonymous !== undefined ? draftData.is_anonymous : true);
        setMaxParticipants(draftData.max_participants || 50);
        
        // Load questions if they exist
        if (draftData.questions && draftData.questions.length > 0) {
          setQuestions(draftData.questions);
        }
        
        toast.success('Draft loaded successfully!');
      } catch (error) {
        console.error('Failed to load draft:', error);
        toast.error('Failed to load draft');
      }
    }
  };

  const processImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Create canvas with 800x800 dimensions
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const targetSize = 800;
          canvas.width = targetSize;
          canvas.height = targetSize;
          
          // Calculate dimensions to maintain aspect ratio and fit in 800x800
          let sourceX = 0;
          let sourceY = 0;
          let sourceWidth = img.width;
          let sourceHeight = img.height;
          
          // Crop to square from center
          if (img.width > img.height) {
            sourceX = (img.width - img.height) / 2;
            sourceWidth = img.height;
          } else if (img.height > img.width) {
            sourceY = (img.height - img.width) / 2;
            sourceHeight = img.width;
          }
          
          // Draw image on canvas (cropped and scaled)
          ctx.drawImage(
            img,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            targetSize,
            targetSize
          );
          
          // Function to compress image to target size
          const compressImage = (quality) => {
            return new Promise((resolveCompress) => {
              canvas.toBlob(
                (blob) => {
                  resolveCompress(blob);
                },
                'image/jpeg',
                quality
              );
            });
          };
          
          // Try to get under 200KB with iterative compression
          const tryCompress = async () => {
            let quality = 0.9;
            let blob = await compressImage(quality);
            const maxSize = 200 * 1024; // 200KB
            
            // Reduce quality until size is under 200KB
            while (blob.size > maxSize && quality > 0.1) {
              quality -= 0.1;
              blob = await compressImage(quality);
            }
            
            if (blob.size > maxSize) {
              reject(new Error('Unable to compress image below 200KB. Please use a simpler image.'));
              return;
            }
            
            // Convert blob to File
            const processedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            
            resolve(processedFile);
          };
          
          tryCompress();
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (file, questionIndex, optionIndex = null) => {
    // Check if user is paid
    if (!profile?.is_paid) {
      setPaywallMessage('Image upload is a premium feature. Upgrade to Premium to add images to your questions and options!');
      setShowPaywallModal(true);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file');
      return;
    }

    setUploadingImage(true);
    try {
      // Process image (resize to 800x800 and compress to <200KB)
      toast.info('Processing image...');
      const processedFile = await processImage(file);
      
      const formData = new FormData();
      formData.append('file', processedFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;

      // Update the appropriate field
      setQuestions(prev => {
        const updated = [...prev];
        if (optionIndex !== null) {
          // Update option image
          updated[questionIndex].options[optionIndex].image_url = imageUrl;
        } else {
          // Update question image
          updated[questionIndex].image_url = imageUrl;
        }
        return updated;
      });

      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const addHashtag = () => {
    if (hashtagInput.trim() && !hashtags.includes(hashtagInput.trim())) {
      setHashtags([...hashtags, hashtagInput.trim()]);
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(t => t !== tag));
  };

  const addQuestion = () => {
    const now = Date.now();
    const lastAddTime = addQuestionTimeRef.current;
    
    // Prevent double-add from React.StrictMode (within 50ms)
    if (now - lastAddTime < 50) {
      console.log('Prevented duplicate addQuestion call');
      return;
    }
    
    addQuestionTimeRef.current = now;
    
    const maxQuestions = profile?.is_paid ? 100 : 5;
    if (questions.length >= maxQuestions) {
      toast.error(`${profile?.is_paid ? 'Premium' : 'Free'} users can add up to ${maxQuestions} questions. ${!profile?.is_paid ? 'Upgrade to add more!' : ''}`);
      if (!profile?.is_paid) {
        navigate('/subscription');
      }
      return;
    }

    setQuestions(prev => [...prev, {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: '',
      image_url: null,
      response_type: 'mcq_response',
      options: [
        { id: Date.now().toString() + '_1', text: '', image_url: null },
        { id: Date.now().toString() + '_2', text: '', image_url: null }
      ],
      randomize_options: false,
      correct_answer: null,
      points: 10,
      points_deducted: 0,
      time_limit: 30,
      order: prev.length
    }]);
  };

  const removeQuestion = (index) => {
    if (questions.length === 1) {
      toast.error('You must have at least one question');
      return;
    }
    setQuestions(prev => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })));
  };

  const resetQuestions = () => {
    if (window.confirm('Are you sure you want to reset all questions? This cannot be undone.')) {
      setQuestions([
        {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: '',
          image_url: null,
          response_type: 'mcq_response',
          options: [
            { id: Date.now().toString() + '_1', text: '', image_url: null },
            { id: Date.now().toString() + '_2', text: '', image_url: null }
          ],
          randomize_options: false,
          correct_answer: null,
          points: null,
          order: 0
        }
      ]);
      toast.success('All questions have been reset');
    }
  };

  const updateQuestion = (index, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      
      // If changing response type, reset options
      if (field === 'response_type') {
        if (value === 'mcq_response' || value === 'image_mcq_response' || value === 'drag_reorder_ranking') {
          if (updated[index].options.length === 0) {
            const timestamp = Date.now();
            updated[index].options = [
              { id: `${timestamp}_1_${Math.random().toString(36).substr(2, 9)}`, text: '', image_url: null },
              { id: `${timestamp}_2_${Math.random().toString(36).substr(2, 9)}`, text: '', image_url: null }
            ];
          }
        } else {
          updated[index].options = [];
        }
      }
      
      // If setting correct_answer and points is null, auto-set to 1
      if (field === 'correct_answer' && value && !updated[index].points) {
        updated[index].points = 1;
      }
      
      // If clearing correct_answer, optionally clear points
      if (field === 'correct_answer' && !value) {
        updated[index].points = null;
      }
      
      return updated;
    });
  };

  const addOption = (questionIndex) => {
    const now = Date.now();
    const lastAddTime = addOptionTimeRef.current[questionIndex] || 0;
    
    // Prevent double-add from React.StrictMode (within 50ms)
    if (now - lastAddTime < 50) {
      console.log('Prevented duplicate addOption call');
      return;
    }
    
    addOptionTimeRef.current[questionIndex] = now;
    
    setQuestions(prev => {
      const updated = [...prev];
      const newId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      updated[questionIndex].options.push({
        id: newId,
        text: '',
        image_url: null
      });
      return updated;
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    setQuestions(prev => {
      const updated = [...prev];
      if (updated[questionIndex].options.length > 2) {
        updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
      } else {
        toast.error('You must have at least 2 options');
      }
      return updated;
    });
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[questionIndex].options[optionIndex][field] = value;
      return updated;
    });
  };

  const handleResponseTypeChange = (index, newType) => {
    // Check if premium response type
    const premiumTypes = ['image_mcq_response', 'drag_reorder_ranking'];
    if (premiumTypes.includes(newType) && !profile?.is_paid) {
      const featureName = newType === 'image_mcq_response' ? 'MCQ with Images' : 'Ranking of Options';
      setPaywallMessage(`${featureName} is a premium feature. Upgrade to Premium to unlock advanced question types!`);
      setShowPaywallModal(true);
      return;
    }
    updateQuestion(index, 'response_type', newType);
  };

  const handlePublishAndStart = async () => {
    // Validate before publishing
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    if (hashtags.length === 0) {
      toast.error('Please add at least one hashtag');
      return;
    }

    // Validate questions
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.text.trim()) {
        toast.error(`Question ${i + 1} text is required`);
        return;
      }

      // Validate options for MCQ/ranking types
      if (['mcq_response', 'image_mcq_response', 'drag_reorder_ranking'].includes(q.response_type)) {
        if (q.options.length < 2) {
          toast.error(`Question ${i + 1} must have at least 2 options`);
          return;
        }
        for (let j = 0; j < q.options.length; j++) {
          if (!q.options[j].text.trim() && !q.options[j].image_url) {
            toast.error(`Question ${i + 1}, Option ${j + 1} must have text or image`);
            return;
          }
        }
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // First, ensure draft is saved and get the draft ID
      const sessionIdToPublish = draftId || await autoSaveDraft();
      
      // Double-check we have a valid draft ID
      if (!sessionIdToPublish) {
        throw new Error('Failed to create draft session');
      }
      
      console.log('[PUBLISH] Publishing session:', sessionIdToPublish);
      
      // Publish the draft
      await axios.post(
        `${API}/sessions/${sessionIdToPublish}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success('Speed Run Quiz created successfully!');
      
      // Navigate to facilitator page
      console.log('[PUBLISH] Navigating to facilitator page for session:', sessionIdToPublish);
      navigate(`/speed-run/${sessionIdToPublish}/facilitator`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Speed Run Quiz');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    await handlePublishAndStart();
  };

  const copyToClipboard = async (text) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopiedLink(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        // Fallback method for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          textArea.remove();
          setCopiedLink(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setCopiedLink(false), 2000);
        } catch (err) {
          textArea.remove();
          // If both methods fail, show the link for manual copy
          toast.error('Unable to copy automatically. Please copy manually.');
          console.error('Failed to copy:', err);
        }
      }
    } catch (err) {
      // Final fallback - try execCommand
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        textArea.remove();
        setCopiedLink(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopiedLink(false), 2000);
      } catch (fallbackErr) {
        textArea.remove();
        toast.error('Unable to copy. Please copy the link manually.');
        console.error('All copy methods failed:', err, fallbackErr);
      }
    }
  };

  // If session is created, show the link and QR code
  if (createdSession) {
    const sessionLink = `${window.location.origin}/participant/${createdSession.id}`;
    
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <div className="flex justify-between items-center mb-4">
            <PollarBrandMini />
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <h1 className="text-3xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
              ✅ Speed Run Quiz Created!
            </h1>
            <p className="text-gray-600 mb-6">
              Share this link or QR code with participants
            </p>

            {/* QR Code */}
            {createdSession.qr_code_data && (
              <div className="mb-6">
                <img 
                  src={createdSession.qr_code_data} 
                  alt="QR Code" 
                  className="mx-auto w-64 h-64"
                />
              </div>
            )}

            {/* Link */}
            <div className="mb-6">
              <Label className="block mb-2 text-sm font-semibold">Participant Link</Label>
              <div className="flex gap-2">
                <Input
                  value={sessionLink}
                  readOnly
                  className="flex-1"
                />
                <Button
                  onClick={() => copyToClipboard(sessionLink)}
                  variant="outline"
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate(`/facilitator/${createdSession.id}`)}
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                Go to Session
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <PollarBrandMini />
        </div>
        
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to Dashboard
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#1a1a1a' }}>
            Setup Speed Run Quiz / Survey
          </h1>
          <p className="text-gray-600 mb-8">
            Create your questionnaire with different question types
          </p>

          <form onSubmit={handleSubmit}>
            {/* Private/Public Toggle */}
            <div className="mb-6">
              <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl border-2 border-purple-200">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    {isPrivate ? '🔒 Private Session' : '🌐 Public Session'}
                    {isPrivate && !profile?.is_paid && (
                      <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white ml-2">
                        <Crown className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </Label>
                  <p className="text-sm text-gray-600 mt-1">
                    {isPrivate 
                      ? 'Only you can see this session. It will not appear in the community feed.'
                      : 'This session will be visible in the community feed after completion.'
                    }
                  </p>
                </div>
                <Switch
                  checked={isPrivate}
                  onCheckedChange={(checked) => {
                    if (checked && !profile?.is_paid) {
                      // Redirect to paywall
                      navigate('/subscription');
                      return;
                    }
                    setIsPrivate(checked);
                  }}
                />
              </div>
            </div>

            {/* Basic Info */}
            <div className="mb-6">
              <Label htmlFor="topic">Quiz Name</Label>
              <Input
                id="topic"
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., Customer Satisfaction Survey"
                className="mt-1"
              />
            </div>

            {/* Hashtags */}
            <div className="mb-6">
              <Label>Hashtags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addHashtag();
                    }
                  }}
                  placeholder="Add hashtag and press Enter"
                  className="flex-1"
                />
                <Button type="button" onClick={addHashtag} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {hashtags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    #{tag}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => removeHashtag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Max Participants */}
            <div className="mb-6">
              <Label className="text-sm text-gray-600 mb-2">Max Participants</Label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="10"
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-lg font-bold min-w-[60px]">{maxParticipants}</span>
              </div>
            </div>

            {/* Anonymous Mode */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Anonymous Participants</Label>
                  <p className="text-xs text-gray-500">Participants use random names instead of real names</p>
                </div>
                <Switch
                  checked={isAnonymous}
                  onCheckedChange={setIsAnonymous}
                />
              </div>
            </div>

            {/* Questions */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <Label className="text-lg font-semibold">
                    Question {questions.length}/{profile?.is_paid ? 100 : 5}
                  </Label>
                  {!profile?.is_paid && (
                    <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 border border-purple-300 rounded-full">
                      <Crown className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-semibold text-purple-700">Premium: Up to 100</span>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={resetQuestions}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Reset All
                </Button>
              </div>

              {questions.map((question, qIndex) => (
                <Card key={question.id} className="mb-4 border-2">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-lg">Question {qIndex + 1}</h3>
                      {questions.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(qIndex)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>

                    {/* Question Text */}
                    <div className="mb-4">
                      <Label>Question Text</Label>
                      <Input
                        type="text"
                        value={question.text}
                        onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                        placeholder="Enter your question"
                        className="mt-1"
                      />
                    </div>

                    {/* Question Image Upload */}
                    <div className="mb-4">
                      <Label className="flex items-center gap-2">
                        Question Image (Optional)
                        {!profile?.is_paid && <Crown className="w-4 h-4 text-yellow-500" />}
                      </Label>
                      {question.image_url ? (
                        <div className="mt-2 relative inline-block">
                          <img src={question.image_url} alt="Question" className="w-32 h-32 object-cover rounded" />
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="absolute -top-2 -right-2"
                            onClick={() => updateQuestion(qIndex, 'image_url', null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleImageUpload(e.target.files[0], qIndex);
                              }
                            }}
                            disabled={uploadingImage}
                            className="hidden"
                            id={`question-image-${qIndex}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              if (!profile?.is_paid) {
                                setPaywallMessage('Image upload is a premium feature. Upgrade to Premium to add images to your questions!');
                                setShowPaywallModal(true);
                                return;
                              }
                              document.getElementById(`question-image-${qIndex}`).click();
                            }}
                            disabled={uploadingImage}
                            className={!profile?.is_paid ? 'border-purple-300 hover:bg-purple-50' : ''}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {uploadingImage ? 'Uploading...' : 'Upload Image'}
                            {!profile?.is_paid && <Crown className="w-4 h-4 ml-2 text-purple-600" />}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Response Type */}
                    <div className="mb-4">
                      <Label>Response Type</Label>
                      <select
                        value={question.response_type}
                        onChange={(e) => handleResponseTypeChange(qIndex, e.target.value)}
                        className="w-full mt-1 p-2 border rounded"
                      >
                        <option value="mcq_response">Multiple Choice</option>
                        <option value="image_mcq_response">{!profile?.is_paid ? '👑 ' : ''}MCQ with Image {!profile?.is_paid ? '(Premium)' : ''}</option>
                        <option value="drag_reorder_ranking">{!profile?.is_paid ? '👑 ' : ''}Ranking of Options {!profile?.is_paid ? '(Premium)' : ''}</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Speed Run Quiz only supports timed competitive question types</p>
                    </div>

                    {/* Options for MCQ/Ranking types */}
                    {['mcq_response', 'image_mcq_response', 'drag_reorder_ranking'].includes(question.response_type) && (
                      <div className="mb-4">
                        <Label>Options</Label>
                        {question.options.map((option, oIndex) => (
                          <div key={option.id} className="mt-2">
                            <div className="flex gap-2 items-start">
                              <Input
                                type="text"
                                value={option.text}
                                onChange={(e) => updateOption(qIndex, oIndex, 'text', e.target.value)}
                                placeholder={`Option ${oIndex + 1}`}
                                className="flex-1"
                              />
                              {question.response_type === 'image_mcq_response' && (
                                <div>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleImageUpload(e.target.files[0], qIndex, oIndex);
                                      }
                                    }}
                                    disabled={uploadingImage}
                                    className="hidden"
                                    id={`option-image-${qIndex}-${oIndex}`}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      if (!profile?.is_paid) {
                                        setPaywallMessage('Image upload for options is a premium feature. Upgrade to Premium to add images to your answer choices!');
                                        setShowPaywallModal(true);
                                        return;
                                      }
                                      document.getElementById(`option-image-${qIndex}-${oIndex}`).click();
                                    }}
                                    disabled={uploadingImage}
                                    className={!profile?.is_paid ? 'border-purple-300' : ''}
                                  >
                                    {option.image_url ? '✓ IMG' : (
                                      <div className="flex items-center gap-1">
                                        <Upload className="w-4 h-4" />
                                        {!profile?.is_paid && <Crown className="w-3 h-3 text-purple-600" />}
                                      </div>
                                    )}
                                  </Button>
                                </div>
                              )}
                              {question.options.length > 2 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeOption(qIndex, oIndex)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                            {/* Show image preview for image MCQ */}
                            {question.response_type === 'image_mcq_response' && option.image_url && (
                              <div className="mt-2 ml-2">
                                <img src={option.image_url} alt={`Option ${oIndex + 1}`} className="w-24 h-24 object-cover rounded" />
                              </div>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addOption(qIndex)}
                          className="mt-2"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Option
                        </Button>

                      </div>
                    )}

                    {/* Time Limit per Question */}
                    <div className="mb-4">
                      <Label className="text-sm font-semibold mb-2 block">Time Limit *</Label>
                      <div className="flex gap-2">
                        {questionTimeLimitOptions.map((opt) => (
                          <Button
                            key={opt.value}
                            type="button"
                            size="sm"
                            variant={question.time_limit === opt.value ? "default" : "outline"}
                            onClick={() => updateQuestion(qIndex, 'time_limit', opt.value)}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Scoring (Required) */}
                    <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                      <Label className="text-sm font-semibold mb-2 block text-orange-700">Scoring (Required) *</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs">Correct Answer</Label>
                          {['mcq_response', 'image_mcq_response'].includes(question.response_type) ? (
                            <select
                              value={question.correct_answer || ''}
                              onChange={(e) => updateQuestion(qIndex, 'correct_answer', e.target.value || null)}
                              className="w-full mt-1 p-2 border rounded text-sm"
                            >
                              <option value="">None</option>
                              {question.options.map((opt, i) => (
                                <option key={opt.id} value={opt.id}>Option {i + 1}</option>
                              ))}
                            </select>
                          ) : question.response_type === 'drag_reorder_ranking' ? (
                            <div>
                              <div className="space-y-1 mt-1">
                                {question.options.map((opt, optIndex) => (
                                  <div key={opt.id} className="flex items-center gap-2 text-sm">
                                    <Input
                                      type="number"
                                      min="1"
                                      max={question.options.length}
                                      placeholder={`#${optIndex + 1}`}
                                      className="w-16 h-8 text-xs"
                                      value={
                                        question.correct_answer
                                          ? question.correct_answer.split(',').indexOf(opt.id) + 1 || ''
                                          : ''
                                      }
                                      onChange={(e) => {
                                        const position = parseInt(e.target.value);
                                        if (position > 0 && position <= question.options.length) {
                                          const currentOrder = question.correct_answer ? question.correct_answer.split(',') : [];
                                          const newOrder = [...currentOrder];
                                          
                                          // Remove this option from current position
                                          const currentPos = newOrder.indexOf(opt.id);
                                          if (currentPos > -1) {
                                            newOrder.splice(currentPos, 1);
                                          }
                                          
                                          // Insert at new position
                                          newOrder.splice(position - 1, 0, opt.id);
                                          
                                          updateQuestion(qIndex, 'correct_answer', newOrder.join(','));
                                        }
                                      }}
                                    />
                                    <span className="flex-1">{opt.text || `Option ${optIndex + 1}`}</span>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-2">Enter position (1 = best, {question.options.length} = worst)</p>
                            </div>
                          ) : (
                            <Input
                              type="text"
                              value={question.correct_answer || ''}
                              onChange={(e) => updateQuestion(qIndex, 'correct_answer', e.target.value || null)}
                              placeholder="e.g., 5"
                              className="mt-1 text-sm"
                            />
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Points if Correct</Label>
                          <Input
                            type="number"
                            value={question.points || ''}
                            onChange={(e) => updateQuestion(qIndex, 'points', e.target.value ? parseInt(e.target.value) : null)}
                            placeholder="e.g., 10"
                            className="mt-1 text-sm"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Points Deducted if Wrong</Label>
                        <Input
                          type="number"
                          value={question.points_deducted || ''}
                          onChange={(e) => updateQuestion(qIndex, 'points_deducted', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="e.g., 5"
                          className="mt-1 text-sm w-48"
                        />
                        <p className="text-xs text-gray-500 mt-1">Optional: Leave 0 for no penalty</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Add More Button */}
              <Button
                type="button"
                variant="outline"
                onClick={addQuestion}
                className="w-full"
                disabled={questions.length >= (profile?.is_paid ? 100 : 5)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add More Question
                {questions.length >= (profile?.is_paid ? 100 : 5) && ' (Limit Reached)'}
              </Button>
            </div>

            {/* Auto-save Indicator */}
            {autoSaving && (
              <div className="text-sm text-gray-500 text-center">
                💾 Saving draft...
              </div>
            )}
            {lastSaved && !autoSaving && (
              <div className="text-sm text-gray-500 text-center">
                ✓ Draft saved {new Date(lastSaved).toLocaleTimeString()}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !draftId}
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {loading ? 'Publishing...' : 'Create & Start Quiz'}
            </Button>
          </form>
        </div>
      </div>

      {/* Premium Feature Paywall Modal */}
      {showPaywallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowPaywallModal(false)}>
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Premium Feature
              </h3>
              <p className="text-gray-600 mb-6">
                {paywallMessage}
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowPaywallModal(false);
                    navigate('/subscription');
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>
                <Button
                  onClick={() => setShowPaywallModal(false)}
                  variant="outline"
                  className="w-full"
                >
                  Continue with Free Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeedRunQuizSetup;
